import React, { useCallback, useMemo, useState } from 'react';
import {
  LuArrowDownToLine, LuArrowUpFromLine, LuBuilding2, LuCircleCheck, LuCirclePause, LuListChecks, LuPencilLine, LuShapes, LuShieldCheck, LuTags, LuTrash2, LuTrendingUp, LuWallet,
} from 'react-icons/lu';
import PageHero from '../components/data/PageHero';
import StatTiles from '../components/data/StatTiles';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import { useTranslation } from '../i18n/LanguageContext';
import ValueListCard from './DataEntryValues/ValueListCard';
import useValueList from './DataEntryValues/useValueList';
import { GROUPS, VALUE_LISTS } from './DataEntryValues/valueLists';

const CONFIG = Object.fromEntries(VALUE_LISTS.map((config) => [config.key, config]));

// Kartların simgesi ve rengi
const LOOK = {
  departments: { icon: LuBuilding2, gradient: 'tw-from-sky-400 tw-to-blue-600 tw-shadow-blue-500/30' },
  roles: { icon: LuShieldCheck, gradient: 'tw-from-violet-400 tw-to-purple-600 tw-shadow-violet-500/30' },
  incomeSources: { icon: LuArrowDownToLine, gradient: 'tw-from-emerald-400 tw-to-teal-600 tw-shadow-emerald-500/30' },
  incomeCategories: { icon: LuTags, gradient: 'tw-from-teal-400 tw-to-cyan-600 tw-shadow-teal-500/30' },
  expenseSources: { icon: LuArrowUpFromLine, gradient: 'tw-from-rose-400 tw-to-rose-600 tw-shadow-rose-500/30' },
  expenseTypes: { icon: LuShapes, gradient: 'tw-from-amber-400 tw-to-orange-500 tw-shadow-amber-500/30' },
  expenseCategories: { icon: LuWallet, gradient: 'tw-from-fuchsia-400 tw-to-pink-600 tw-shadow-fuchsia-500/30' },
};
const GROUP_ICONS = { people: LuShieldCheck, income: LuTrendingUp, expense: LuWallet };

/**
 * Veri Girişi Değerleri (Tailwind): the seven value lists other forms offer
 * (departments, authorization roles, income sources / categories, expense
 * sources / types / categories), grouped, each as a card with its own save.
 * Removing a value that records use offers to make it passive instead (the
 * old rule), now in a dialog.
 */
const DataEntryValuesPage = () => {
  const { t, language } = useTranslation();
  const label = useCallback((key, vars) => t(`dataEntryValues.${key}`, vars), [t]);

  const departments = useValueList(CONFIG.departments, label);
  const roles = useValueList(CONFIG.roles, label);
  const incomeSources = useValueList(CONFIG.incomeSources, label);
  const incomeCategories = useValueList(CONFIG.incomeCategories, label);
  const expenseSources = useValueList(CONFIG.expenseSources, label);
  const expenseTypes = useValueList(CONFIG.expenseTypes, label);
  const expenseCategories = useValueList(CONFIG.expenseCategories, label);
  const lists = { departments, roles, incomeSources, incomeCategories, expenseSources, expenseTypes, expenseCategories };
  const allLists = Object.values(lists);

  const [confirm, setConfirm] = useState(null); // { list, type, uid, value }
  const [busy, setBusy] = useState(false);

  const handleRemove = async (list, uid) => {
    const request = await list.requestRemove(uid);
    if (request) setConfirm({ list, ...request });
  };

  const runConfirm = async () => {
    if (!confirm) return;
    if (confirm.type === 'deactivate') {
      confirm.list.confirmDeactivate(confirm.uid);
      setConfirm(null);
      return;
    }
    setBusy(true);
    await confirm.list.confirmDelete(confirm.uid);
    setBusy(false);
    setConfirm(null);
  };

  const totals = useMemo(() => allLists.reduce((acc, list) => ({
    active: acc.active + list.counts.active,
    passive: acc.passive + list.counts.passive,
    unsaved: acc.unsaved + (list.dirty ? 1 : 0),
  }), { active: 0, passive: 0, unsaved: 0 }), [allLists]);
  const ready = allLists.every((list) => !list.isLoading);

  const tiles = [
    { key: 'lists', label: label('stats.lists'), shortLabel: label('stats.listsShort'), value: VALUE_LISTS.length, icon: LuListChecks, gradient: 'tw-from-indigo-400 tw-to-violet-600 tw-shadow-indigo-500/30' },
    { key: 'active', label: label('stats.active'), shortLabel: label('stats.activeShort'), value: totals.active, icon: LuCircleCheck, gradient: 'tw-from-emerald-400 tw-to-teal-600 tw-shadow-emerald-500/30' },
    { key: 'passive', label: label('stats.passive'), shortLabel: label('stats.passiveShort'), value: totals.passive, icon: LuCirclePause, gradient: 'tw-from-slate-400 tw-to-slate-600 tw-shadow-slate-500/30' },
    { key: 'unsaved', label: label('stats.unsaved'), shortLabel: label('stats.unsavedShort'), value: totals.unsaved, icon: LuPencilLine, gradient: 'tw-from-amber-400 tw-to-orange-500 tw-shadow-amber-500/30' },
  ];

  const confirmKey = confirm ? confirm.list.config.key : null;

  return (
    <>
      <div lang={language} className="tw-mx-auto tw-w-full tw-max-w-[1680px] tw-space-y-5 tw-pb-8 tw-font-sans tw-antialiased md:tw-px-4 lg:tw-px-6 2xl:tw-px-10">
        <PageHero
          icon={LuListChecks}
          gradient="tw-from-indigo-400 tw-to-violet-600 tw-shadow-indigo-500/30"
          title={label('page.title')}
          subtitle={label('page.subtitle')}
        />

        <StatTiles tiles={tiles} ready={ready} className="tw-grid-cols-2 lg:tw-grid-cols-4" />

        {GROUPS.map((group) => {
          const GroupIcon = GROUP_ICONS[group];
          const groupLists = VALUE_LISTS.filter((config) => config.group === group);
          return (
            <section key={group} aria-labelledby={`group-${group}`} className="tw-space-y-3">
              <div className="tw-flex tw-items-center tw-gap-2 tw-px-1">
                <GroupIcon aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-slate-400" />
                <h2 id={`group-${group}`} className="tw-text-sm tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-slate-500 dark:tw-text-slate-400">{label(`groups.${group}`)}</h2>
              </div>
              <div className={`tw-grid tw-gap-4 ${groupLists.length === 3 ? 'lg:tw-grid-cols-2 2xl:tw-grid-cols-3' : 'lg:tw-grid-cols-2'}`}>
                {groupLists.map((config) => (
                  <ValueListCard
                    key={config.key}
                    list={lists[config.key]}
                    icon={LOOK[config.key].icon}
                    gradient={LOOK[config.key].gradient}
                    label={label}
                    onRemove={handleRemove}
                  />
                ))}
              </div>
            </section>
          );
        })}
      </div>

      <ConfirmDialog
        isOpen={Boolean(confirm)}
        title={confirm?.type === 'deactivate' ? label('confirm.deactivateTitle') : label('confirm.deleteTitle')}
        message={confirm ? label(`lists.${confirmKey}.${confirm.type === 'deactivate' ? 'inUse' : 'deleteMessage'}`, { value: confirm.value }) : ''}
        confirmLabel={confirm?.type === 'deactivate' ? label('confirm.deactivate') : label('confirm.delete')}
        cancelLabel={label('confirm.cancel')}
        tone={confirm?.type === 'deactivate' ? 'warning' : 'danger'}
        icon={confirm?.type === 'deactivate' ? LuCirclePause : LuTrash2}
        busy={busy}
        onConfirm={runConfirm}
        onCancel={() => !busy && setConfirm(null)}
      />
    </>
  );
};

export default DataEntryValuesPage;
